import {Menu} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {ServiceEventTicketPolicyEntity} from "@ots-frontend/ots-data";
import {useCallback, useMemo} from "react";
import {useAuth} from "../../../providers/AuthProvider/AuthProvider";
import {IconEdit, IconTrash} from "@tabler/icons-react";
import {useServiceEventTicketPolicyAction} from "./useServiceEventTicketPolicyAction";

const ALL_ACTIONS = [
  "EDIT",
  "DELETE",
] as const;

export type ServiceEventTicketPolicyAction = typeof ALL_ACTIONS[number];

export type ServiceEventTicketPolicyActionMenuDropdownProps = {
  policy: ServiceEventTicketPolicyEntity
  actions?: ServiceEventTicketPolicyAction[]
  onActionClick?: (action: ServiceEventTicketPolicyAction) => void
  showEmpty?: boolean
}

export const ServiceEventTicketPolicyActionMenuDropdown = (props: ServiceEventTicketPolicyActionMenuDropdownProps) => {
  const {
    policy,
    actions = ALL_ACTIONS,
    onActionClick,
    showEmpty = true
  } = props

  const {hasAccess} = useAuth()
  const {t} = useTranslation(["common"])

  const visibleActions: Set<ServiceEventTicketPolicyAction> = useMemo(() => {
    const enabledActions = new Set<ServiceEventTicketPolicyAction>()
    const actionsSet = actions ? new Set<ServiceEventTicketPolicyAction>(actions) : new Set<ServiceEventTicketPolicyAction>()

    if (hasAccess("serviceeventticketpolicy_edit")) {
      if (actionsSet.has("EDIT")) {
        enabledActions.add("EDIT")
      }
    }

    if (hasAccess("serviceeventticketpolicy_delete")) {
      if (actionsSet.has("DELETE")) {
        enabledActions.add("DELETE")
      }
    }

    return enabledActions
  }, [policy, actions, hasAccess])

  const {
    onServiceEventTicketPolicyActionClick
  } = useServiceEventTicketPolicyAction()

  const _onActionClick = useCallback((action: ServiceEventTicketPolicyAction) => {
    if (onActionClick) {
      onActionClick(action)
    } else {
      onServiceEventTicketPolicyActionClick(policy, action)
    }
  }, [onActionClick, onServiceEventTicketPolicyActionClick, policy])

  return (
    <>
      {(showEmpty && visibleActions.size === 0) &&
        <Menu.Item>
          {t("common.action.none", "No Actions")}
        </Menu.Item>
      }
      {visibleActions.has("EDIT") &&
        <Menu.Item icon={<IconEdit size={14}/>} onClick={() => _onActionClick("EDIT")}>
          {t("common.action.edit", "Edit")}
        </Menu.Item>
      }
      {visibleActions.has("DELETE") &&
        <>
          {visibleActions.size > 1 && <Menu.Divider/>}
          <Menu.Item
            onClick={() => _onActionClick("DELETE")}
            icon={<IconTrash size={14}/>}
            color="red">
            {t("common.action.delete", "Delete")}
          </Menu.Item>
        </>
      }
    </>
  )
}
